import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import EligibilityForm, { EligibilityFormData } from '../components/EligibilityForm';
import './BuyHome.css';

const BuyHome: React.FC = () => {
  const [openFAQ, setOpenFAQ] = useState<number | null>(0);
  const [isEligibilityFormOpen, setIsEligibilityFormOpen] = useState<boolean>(false);
  const [statsBackgroundImage, setStatsBackgroundImage] = useState<string>('');
  const [heroImage, setHeroImage] = useState<string>('');
  const [whyChooseUsImage, setWhyChooseUsImage] = useState<string>('');
  const [loanTypesImage, setLoanTypesImage] = useState<string>('');
  const [contactAvatarImage, setContactAvatarImage] = useState<string>('');
  const [socialProofBackgroundImage, setSocialProofBackgroundImage] = useState<string>('');
  
  const defaultStatsImage = 'https://api.builder.io/api/v1/image/assets/TEMP/46b7939e5bebf1b51c9bf6b1599f55e6c8e8dbea?width=2880';
  const defaultPlaceholder = 'https://api.builder.io/api/v1/image/assets/TEMP/placeholder';

  useEffect(() => {
    // Load stats background image
    const statsImg = new Image();
    statsImg.src = '/assets/stats/cta.jpg';
    statsImg.onload = () => setStatsBackgroundImage(`url(${statsImg.src})`);
    statsImg.onerror = () => setStatsBackgroundImage(`url(${defaultStatsImage})`);

    // Load hero image
    const heroImg = new Image();
    heroImg.src = '/assets/buy-home/hero/villa.png';
    heroImg.onload = () => setHeroImage(heroImg.src);
    heroImg.onerror = () => setHeroImage(defaultPlaceholder);

    // Load why choose us image
    const whyImg = new Image();
    whyImg.src = '/assets/buy-home/why-choose-us/house.jpg';
    whyImg.onload = () => setWhyChooseUsImage(whyImg.src);
    whyImg.onerror = () => setWhyChooseUsImage(defaultPlaceholder);

    // Load loan types image
    const loanImg = new Image();
    loanImg.src = '/assets/buy-home/loan-types/loan.png';
    loanImg.onload = () => setLoanTypesImage(loanImg.src);
    loanImg.onerror = () => setLoanTypesImage(defaultPlaceholder);

    // Load contact avatar image
    const contactImg = new Image();
    contactImg.src = '/assets/buy-home/faq-contact/contact.jpg';
    contactImg.onload = () => setContactAvatarImage(contactImg.src);
    contactImg.onerror = () => {
      // Try alternative name
      const altImg = new Image();
      altImg.src = '/assets/buy-home/faq-contact/avatar.jpg';
      altImg.onload = () => setContactAvatarImage(altImg.src);
      altImg.onerror = () => setContactAvatarImage(defaultPlaceholder);
    };

    // Load social proof background image
    const socialProofImg = new Image();
    socialProofImg.src = '/assets/buy-home/social-proof/house.png';
    socialProofImg.onload = () => setSocialProofBackgroundImage(`url(${socialProofImg.src})`);
    socialProofImg.onerror = () => {
      // Try alternative names
      const altImg1 = new Image();
      altImg1.src = '/assets/buy-home/social-proof/house.jpg';
      altImg1.onload = () => setSocialProofBackgroundImage(`url(${altImg1.src})`);
      altImg1.onerror = () => {
        const altImg2 = new Image();
        altImg2.src = '/assets/buy-home/social-proof/background.jpg';
        altImg2.onload = () => setSocialProofBackgroundImage(`url(${altImg2.src})`);
        altImg2.onerror = () => setSocialProofBackgroundImage('');
      };
    };
  }, []);

  const toggleFAQ = (index: number) => {
    setOpenFAQ(openFAQ === index ? null : index);
  };

  const handleEligibilityFormSubmit = (formData: EligibilityFormData) => {
    // TODO: Implement form submission logic (e.g., send to API)
    console.log('Form submitted with data:', formData);
    // Don't close the form here - let the user see step 8 (thank you page)
    // The form will close when user clicks "Close" button on step 8
  };

  const faqs = [
    {
      question: 'How much do I need for a down payment?',
      answer: 'Typically, a down payment ranges from 3% to 20% of the home\'s price, depending on the loan type. Some programs offer low or no down payment options.'
    },
    {
      question: 'What\'s the difference between fixed and adjustable-rate mortgages?',
      answer: 'A fixed-rate mortgage has the same interest rate for the life of the loan, while an adjustable-rate mortgage (ARM) has a rate that may change periodically after an initial fixed period.'
    },
    {
      question: 'How do I qualify for a mortgage?',
      answer: 'Mortgage qualification is based on factors like your credit score, income, debt-to-income ratio, and employment history. We\'ll help you understand what\'s needed and guide you through the process.'
    },
    {
      question: 'What credit score do I need to qualify for a mortgage?',
      answer: 'On average, it takes 30-45 days to close on a home, though it may vary depending on the specifics of your loan and the homebuying process.'
    },
    {
      question: 'How long does the mortgage approval process usually take?',
      answer: 'Yes, getting pre-approved helps you understand your budget and shows sellers that you\'re a serious buyer, giving you an advantage in competitive markets.'
    }
  ];

  return (
    <div className="buy-home-page">
      <Navbar />
      <main className="buy-home-main">
        {/* Hero Section */}
        <section className="buy-home-hero">
          <div className="buy-home-container">
            <div className="buy-home-hero-content">
              <div className="buy-home-hero-text">
                <h1 className="buy-home-hero-title">
                  Make your home ownership dream a reality
                </h1>
                <p className="buy-home-hero-description">
                  We offer expert guidance and personalized mortgage solutions, ensuring you get the best rates and terms that fit your financial needs—whether you're a first-time buyer or upgrading your home.
                </p>
                <div className="buy-home-hero-ctas">
                  <button 
                    className="buy-home-cta-primary"
                    onClick={() => setIsEligibilityFormOpen(true)}
                  >
                    Check your eligibility
                  </button>
                  <button className="buy-home-cta-secondary">Contact us</button>
                </div>
                <p className="buy-home-trust-line">
                  Trusted by over <strong>1,000 happy homeowners</strong>, with an average satisfaction rating of <strong>4.9/5</strong>.
                </p>
              </div>
              <div className="buy-home-hero-image">
                <img 
                  src={heroImage || defaultPlaceholder} 
                  alt="Home ownership" 
                  onError={(e) => {
                    const target = e.target as HTMLImageElement;
                    target.src = defaultPlaceholder;
                  }}
                />
              </div>
            </div>
          </div>
        </section>

        {/* Partner Logos Section */}
        <section className="buy-home-partners">
          <div className="buy-home-container">
            <div className="buy-home-partners-grid">
              {/* Partner logos will be displayed here */}
            </div>
          </div>
        </section>

        {/* 4-Step Process Section */}
        <section className="buy-home-process">
          <div className="buy-home-container">
            <div className="buy-home-process-header">
              <h2 className="buy-home-section-title">Our 4-Step Home Buying Process</h2>
              <p className="buy-home-section-description">
                We've simplified the home-buying journey to make it as smooth and stress-free as possible. Our 4-step process is designed to guide you from start to finish, ensuring you get the best mortgage solution and close on your dream home without hassle.
              </p>
            </div>
            <div className="buy-home-steps-grid">
              <div className="buy-home-step-card">
                <div className="buy-home-step-icon">
                  <svg width="48" height="48" viewBox="0 0 48 48" fill="none">
                    <path d="M24 4L6 14V24C6 32.84 11.16 41.16 24 44C36.84 41.16 42 32.84 42 24V14L24 4Z" fill="#0578EC"/>
                  </svg>
                </div>
                <h3 className="buy-home-step-title">Get Pre-Approved</h3>
                <p className="buy-home-step-description">We assess your financial situation and pre-qualify you.</p>
              </div>
              <div className="buy-home-step-card">
                <div className="buy-home-step-icon">
                  <svg width="48" height="48" viewBox="0 0 48 48" fill="none">
                    <path d="M24 4L6 14V24C6 32.84 11.16 41.16 24 44C36.84 41.16 42 32.84 42 24V14L24 4Z" fill="#0578EC"/>
                  </svg>
                </div>
                <h3 className="buy-home-step-title">Find Your Home</h3>
                <p className="buy-home-step-description">Work with our trusted real estate partners to find the perfect house.</p>
              </div>
              <div className="buy-home-step-card">
                <div className="buy-home-step-icon">
                  <svg width="48" height="48" viewBox="0 0 48 48" fill="none">
                    <path d="M24 4L6 14V24C6 32.84 11.16 41.16 24 44C36.84 41.16 42 32.84 42 24V14L24 4Z" fill="#0578EC"/>
                  </svg>
                </div>
                <h3 className="buy-home-step-title">Choose Your Loan</h3>
                <p className="buy-home-step-description">We'll help you select the best mortgage product.</p>
              </div>
              <div className="buy-home-step-card">
                <div className="buy-home-step-icon">
                  <svg width="48" height="48" viewBox="0 0 48 48" fill="none">
                    <path d="M24 4L6 14V24C6 32.84 11.16 41.16 24 44C36.84 41.16 42 32.84 42 24V14L24 4Z" fill="#0578EC"/>
                  </svg>
                </div>
                <h3 className="buy-home-step-title">Close the Deal</h3>
                <p className="buy-home-step-description">We ensure a smooth closing process for a successful purchase.</p>
              </div>
            </div>
            <div className="buy-home-process-cta">
              <button className="buy-home-cta-primary">Get started today</button>
            </div>
          </div>
        </section>

        {/* Why Choose Us Section */}
        <section className="buy-home-why">
          <div className="buy-home-container">
            <div className="buy-home-why-content">
              <div className="buy-home-why-text">
                <h2 className="buy-home-section-title">Why choose us to buy your home?</h2>
                <div className="buy-home-benefits-list">
                  <div className="buy-home-benefit-item">
                    <div className="buy-home-star-icon">
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z" fill="#0578EC"/>
                      </svg>
                    </div>
                    <p>
                      <strong>Tailored Loan Options: </strong>
                      We offer a variety of loan programs to fit your unique situation, from first-time homebuyers to seasoned investors.
                    </p>
                  </div>
                  <div className="buy-home-benefit-item">
                    <div className="buy-home-star-icon">
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z" fill="#0578EC"/>
                      </svg>
                    </div>
                    <p>
                      <strong>Better Rates, Better Terms:</strong>{' '}
                      We find competitive mortgage rates to save you money.
                    </p>
                  </div>
                  <div className="buy-home-benefit-item">
                    <div className="buy-home-star-icon">
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z" fill="#0578EC"/>
                      </svg>
                    </div>
                    <p>
                      <strong>Fast and Efficient Approval:</strong>{' '}
                      Our streamlined process ensures quicker approval times, helping you move into your new home faster.
                    </p>
                  </div>
                  <div className="buy-home-benefit-item">
                    <div className="buy-home-star-icon">
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z" fill="#0578EC"/>
                      </svg>
                    </div>
                    <p>
                      <strong>Smooth, Stress-Free Process:</strong>{' '}
                      We simplify the process to make home-buying easy.
                    </p>
                  </div>
                </div>
                <div className="buy-home-why-ctas">
                  <button className="buy-home-cta-primary">Get your rate</button>
                  <button className="buy-home-cta-secondary">
                    <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                      <path d="M6.28521 6.64001C6.80721 7.72723 7.5188 8.74621 8.41998 9.64739C9.32116 10.5486 10.3401 11.2602 11.4274 11.7822C11.5209 11.8271 11.5676 11.8495 11.6268 11.8668C11.8371 11.9281 12.0953 11.884 12.2733 11.7565C12.3234 11.7206 12.3663 11.6778 12.452 11.592C12.7143 11.3298 12.8454 11.1987 12.9772 11.113C13.4744 10.7898 14.1153 10.7898 14.6125 11.113C14.7443 11.1987 14.8754 11.3298 15.1376 11.592L15.2838 11.7382C15.6823 12.1368 15.8816 12.3361 15.9899 12.5501C16.2052 12.9758 16.2052 13.4785 15.9899 13.9041C15.8816 14.1182 15.6823 14.3175 15.2838 14.7161L15.1655 14.8343C14.7683 15.2315 14.5697 15.4301 14.2997 15.5818C14 15.7501 13.5347 15.8711 13.191 15.8701C12.8813 15.8692 12.6696 15.8091 12.2463 15.6889C9.97125 15.0432 7.82448 13.8249 6.0335 12.0339C4.24251 10.2429 3.02415 8.09612 2.37843 5.82108C2.25827 5.39774 2.19819 5.18607 2.19727 4.87636C2.19625 4.5327 2.31727 4.06733 2.48559 3.7677C2.63727 3.49768 2.83588 3.29907 3.2331 2.90185L3.35132 2.78362C3.74991 2.38504 3.9492 2.18575 4.16323 2.07749C4.5889 1.86219 5.0916 1.86219 5.51727 2.07749C5.73131 2.18575 5.9306 2.38504 6.32918 2.78362L6.47533 2.92978C6.73754 3.19198 6.86864 3.32308 6.95436 3.45492C7.2776 3.95208 7.2776 4.59302 6.95435 5.09019C6.86864 5.22202 6.73754 5.35312 6.47533 5.61533C6.3896 5.70106 6.34673 5.74393 6.31085 5.79404C6.18334 5.97211 6.13932 6.2303 6.20061 6.44057C6.21786 6.49974 6.24031 6.5465 6.28521 6.64001Z" stroke="black" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                    Contact us
                  </button>
                </div>
              </div>
              <div className="buy-home-why-image">
                <img 
                  src={whyChooseUsImage || defaultPlaceholder} 
                  alt="Why choose us" 
                  onError={(e) => {
                    const target = e.target as HTMLImageElement;
                    target.src = defaultPlaceholder;
                  }}
                />
              </div>
            </div>
          </div>
        </section>

        {/* Stats Section */}
        <section className="buy-home-stats">
          <div className="buy-home-stats-wrapper">
            <div 
              className="buy-home-stats-image"
              style={{
                backgroundImage: statsBackgroundImage || undefined,
                backgroundSize: statsBackgroundImage ? 'cover' : undefined,
                backgroundPosition: statsBackgroundImage ? 'center' : undefined,
                backgroundRepeat: statsBackgroundImage ? 'no-repeat' : undefined
              }}
            >
              <div className="buy-home-stats-image-fade"></div>
            </div>
            <div className="buy-home-stats-content-wrapper">
              <div className="buy-home-container">
                <div className="buy-home-stats-content">
                  <div className="buy-home-stats-text">
                    <h2 className="buy-home-stats-title">We will find best rate</h2>
                    <div className="buy-home-stats-ctas">
                      <button className="buy-home-cta-primary">Get your rate</button>
                      <button className="buy-home-cta-secondary">
                        <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                          <path d="M6.28521 6.64001C6.80721 7.72723 7.5188 8.74621 8.41998 9.64739C9.32116 10.5486 10.3401 11.2602 11.4274 11.7822C11.5209 11.8271 11.5676 11.8495 11.6268 11.8668C11.8371 11.9281 12.0953 11.884 12.2733 11.7565C12.3234 11.7206 12.3663 11.6778 12.452 11.592C12.7143 11.3298 12.8454 11.1987 12.9772 11.113C13.4744 10.7898 14.1153 10.7898 14.6125 11.113C14.7443 11.1987 14.8754 11.3298 15.1376 11.592L15.2838 11.7382C15.6823 12.1368 15.8816 12.3361 15.9899 12.5501C16.2052 12.9758 16.2052 13.4785 15.9899 13.9041C15.8816 14.1182 15.6823 14.3175 15.2838 14.7161L15.1655 14.8343C14.7683 15.2315 14.5697 15.4301 14.2997 15.5818C14 15.7501 13.5347 15.8711 13.191 15.8701C12.8813 15.8692 12.6696 15.8091 12.2463 15.6889C9.97125 15.0432 7.82448 13.8249 6.0335 12.0339C4.24251 10.2429 3.02415 8.09612 2.37843 5.82108C2.25827 5.39774 2.19819 5.18607 2.19727 4.87636C2.19625 4.5327 2.31727 4.06733 2.48559 3.7677C2.63727 3.49768 2.83588 3.29907 3.2331 2.90185L3.35132 2.78362C3.74991 2.38504 3.9492 2.18575 4.16323 2.07749C4.5889 1.86219 5.0916 1.86219 5.51727 2.07749C5.73131 2.18575 5.9306 2.38504 6.32918 2.78362L6.47533 2.92978C6.73754 3.19198 6.86864 3.32308 6.95436 3.45492C7.2776 3.95208 7.2776 4.59302 6.95435 5.09019C6.86864 5.22202 6.73754 5.35312 6.47533 5.61533C6.3896 5.70106 6.34673 5.74393 6.31085 5.79404C6.18334 5.97211 6.13932 6.2303 6.20061 6.44057C6.21786 6.49974 6.24031 6.5465 6.28521 6.64001Z" stroke="black" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                        Contact us
                      </button>
                    </div>
                    <div className="buy-home-stats-grid">
                      <div className="buy-home-stat-item">
                        <div className="buy-home-stat-number">17+</div>
                        <div className="buy-home-stat-label">years in experience</div>
                      </div>
                      <div className="buy-home-stat-item">
                        <div className="buy-home-stat-number">128+</div>
                        <div className="buy-home-stat-label">helped clients</div>
                      </div>
                      <div className="buy-home-stat-item">
                        <div className="buy-home-stat-number">96%</div>
                        <div className="buy-home-stat-label">satisfaction rate</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Loan Types Section */}
        <section className="buy-home-loans">
          <div className="buy-home-container">
            <div className="buy-home-loans-content">
              <div className="buy-home-loans-text">
                <h2 className="buy-home-section-title">Find the right loan for you</h2>
                <p className="buy-home-section-description">
                  Whether you're a first-time homebuyer, a veteran, or someone with a less-than-perfect credit score, we have solutions for you.
                </p>
                <div className="buy-home-loan-types">
                  <div className="buy-home-loan-type-item">
                    <div className="buy-home-loan-type-header">
                      <span className="buy-home-loan-icon">+</span>
                      <h3 className="buy-home-loan-type-title">Conventional Loans</h3>
                    </div>
                    <p className="buy-home-loan-type-description">
                      Perfect for buyers with a solid credit score and down payment. Great for primary, secondary, or investment residences
                    </p>
                  </div>
                  <div className="buy-home-loan-type-item">
                    <div className="buy-home-loan-type-header">
                      <span className="buy-home-loan-icon">+</span>
                      <h3 className="buy-home-loan-type-title">FHA Loans</h3>
                    </div>
                  </div>
                  <div className="buy-home-loan-type-item">
                    <div className="buy-home-loan-type-header">
                      <span className="buy-home-loan-icon">+</span>
                      <h3 className="buy-home-loan-type-title">VA Loans</h3>
                    </div>
                  </div>
                  <div className="buy-home-loan-type-item">
                    <div className="buy-home-loan-type-header">
                      <span className="buy-home-loan-icon">+</span>
                      <h3 className="buy-home-loan-type-title">Jumbo Loans</h3>
                    </div>
                  </div>
                </div>
                <div className="buy-home-loans-ctas">
                  <button className="buy-home-cta-primary">Get your rate</button>
                  <button className="buy-home-cta-secondary">
                    <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                      <path d="M6.28521 6.64001C6.80721 7.72723 7.5188 8.74621 8.41998 9.64739C9.32116 10.5486 10.3401 11.2602 11.4274 11.7822C11.5209 11.8271 11.5676 11.8495 11.6268 11.8668C11.8371 11.9281 12.0953 11.884 12.2733 11.7565C12.3234 11.7206 12.3663 11.6778 12.452 11.592C12.7143 11.3298 12.8454 11.1987 12.9772 11.113C13.4744 10.7898 14.1153 10.7898 14.6125 11.113C14.7443 11.1987 14.8754 11.3298 15.1376 11.592L15.2838 11.7382C15.6823 12.1368 15.8816 12.3361 15.9899 12.5501C16.2052 12.9758 16.2052 13.4785 15.9899 13.9041C15.8816 14.1182 15.6823 14.3175 15.2838 14.7161L15.1655 14.8343C14.7683 15.2315 14.5697 15.4301 14.2997 15.5818C14 15.7501 13.5347 15.8711 13.191 15.8701C12.8813 15.8692 12.6696 15.8091 12.2463 15.6889C9.97125 15.0432 7.82448 13.8249 6.0335 12.0339C4.24251 10.2429 3.02415 8.09612 2.37843 5.82108C2.25827 5.39774 2.19819 5.18607 2.19727 4.87636C2.19625 4.5327 2.31727 4.06733 2.48559 3.7677C2.63727 3.49768 2.83588 3.29907 3.2331 2.90185L3.35132 2.78362C3.74991 2.38504 3.9492 2.18575 4.16323 2.07749C4.5889 1.86219 5.0916 1.86219 5.51727 2.07749C5.73131 2.18575 5.9306 2.38504 6.32918 2.78362L6.47533 2.92978C6.73754 3.19198 6.86864 3.32308 6.95436 3.45492C7.2776 3.95208 7.2776 4.59302 6.95435 5.09019C6.86864 5.22202 6.73754 5.35312 6.47533 5.61533C6.3896 5.70106 6.34673 5.74393 6.31085 5.79404C6.18334 5.97211 6.13932 6.2303 6.20061 6.44057C6.21786 6.49974 6.24031 6.5465 6.28521 6.64001Z" stroke="black" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                    Contact us
                  </button>
                </div>
              </div>
              <div className="buy-home-loans-image">
                <img 
                  src={loanTypesImage || defaultPlaceholder} 
                  alt="Loan types" 
                  onError={(e) => {
                    const target = e.target as HTMLImageElement;
                    target.src = defaultPlaceholder;
                  }}
                />
              </div>
            </div>
          </div>
        </section>

        {/* Social Proof Section */}
        <section 
          className="buy-home-social-proof"
          style={{
            backgroundImage: socialProofBackgroundImage || undefined,
            backgroundSize: socialProofBackgroundImage ? 'cover' : undefined,
            backgroundPosition: socialProofBackgroundImage ? 'center' : undefined,
            backgroundRepeat: socialProofBackgroundImage ? 'no-repeat' : undefined
          }}
        >
          <div className="buy-home-social-proof-container">
            <div className="buy-home-social-proof-badge">
              <p>
                Join <strong>hundreds of satisfied homeowners</strong> who have trusted us to secure their financial future. Rated <strong>4.9/5</strong> for delivering personalized mortgage solutions, from buying a home to refinancing and reverse mortgages.
              </p>
            </div>
          </div>
        </section>

        {/* Testimonials Section */}
        <section className="buy-home-testimonials">
          <div className="buy-home-container">
            <div className="buy-home-testimonials-grid">
              <div className="buy-home-testimonial">
                <div className="buy-home-testimonial-divider">
                  <div className="buy-home-testimonial-divider-line"></div>
                </div>
                <div className="buy-home-testimonial-content">
                  <div className="buy-home-testimonial-stars">
                    {[...Array(5)].map((_, i) => (
                      <svg key={i} width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M11.9996 17.8441L6.18281 20.9021L7.29371 14.4251L2.58786 9.838L9.09119 8.89301L11.9996 3L14.9079 8.89301L21.4113 9.838L16.7054 14.4251L17.8163 20.9021L11.9996 17.8441Z" fill="#0578EC"/>
                      </svg>
                    ))}
                  </div>
                  <p className="buy-home-testimonial-author">
                    <strong>Elena Tuttle</strong> <span className="buy-home-testimonial-location">from Tustin</span>
                  </p>
                  <p className="buy-home-testimonial-text">
                    "Above and beyond. We had a very complex 1031 exchange and multiple mortgages with multiple entities. Stephan and his team worked tirelessly to ensure we closed on our properties"
                  </p>
                </div>
              </div>
              <div className="buy-home-testimonial">
                <div className="buy-home-testimonial-divider">
                  <div className="buy-home-testimonial-divider-line"></div>
                </div>
                <div className="buy-home-testimonial-content">
                  <div className="buy-home-testimonial-stars">
                    {[...Array(5)].map((_, i) => (
                      <svg key={i} width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M11.9996 17.8441L6.18281 20.9021L7.29371 14.4251L2.58786 9.838L9.09119 8.89301L11.9996 3L14.9079 8.89301L21.4113 9.838L16.7054 14.4251L17.8163 20.9021L11.9996 17.8441Z" fill="#0578EC"/>
                      </svg>
                    ))}
                  </div>
                  <p className="buy-home-testimonial-author">
                    <strong>Dan May</strong> <span className="buy-home-testimonial-location">from Costa Mesa</span>
                  </p>
                  <p className="buy-home-testimonial-text">
                    "My go to resource for everything mortgage related. Prompt service, valuable experience, competitive pricing.  Highly recommend.  You owe it to yourself and family to use The Best!"
                  </p>
                </div>
              </div>
              <div className="buy-home-testimonial">
                <div className="buy-home-testimonial-divider">
                  <div className="buy-home-testimonial-divider-line"></div>
                </div>
                <div className="buy-home-testimonial-content">
                  <div className="buy-home-testimonial-stars">
                    {[...Array(5)].map((_, i) => (
                      <svg key={i} width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M11.9996 17.8441L6.18281 20.9021L7.29371 14.4251L2.58786 9.838L9.09119 8.89301L11.9996 3L14.9079 8.89301L21.4113 9.838L16.7054 14.4251L17.8163 20.9021L11.9996 17.8441Z" fill="#0578EC"/>
                      </svg>
                    ))}
                  </div>
                  <p className="buy-home-testimonial-author">
                    <strong>Agent Ivy</strong> <span className="buy-home-testimonial-location">from Irvine</span>
                  </p>
                  <p className="buy-home-testimonial-text">
                    "His strategic helped us procure a good loan at the best available rate. He took an interest in my concerns and my fears and offered me incredibly kind and helpful feedback."
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="buy-home-faq">
          <div className="buy-home-container">
            <div className="buy-home-faq-content">
              <h2 className="buy-home-faq-title">
                Have questions?<br />Find answer.
              </h2>
              <div className="buy-home-faq-list">
                {faqs.map((faq, index) => (
                  <div key={index} className="buy-home-faq-item">
                    <button 
                      className="buy-home-faq-question"
                      onClick={() => toggleFAQ(index)}
                    >
                      <span>{faq.question}</span>
                      <svg 
                        className={`buy-home-faq-icon ${openFAQ === index ? 'open' : ''}`}
                        width="24" 
                        height="24" 
                        viewBox="0 0 24 24" 
                        fill="none"
                      >
                        <path d="M18 15L12 9L6 15" stroke="#0578EC" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </button>
                    {openFAQ === index && (
                      <div className="buy-home-faq-answer">
                        <p>{faq.answer}</p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
              <div className="buy-home-faq-contact">
                <div className="buy-home-faq-contact-avatar">
                  <img 
                    src={contactAvatarImage || defaultPlaceholder} 
                    alt="Contact" 
                    onError={(e) => {
                      const target = e.target as HTMLImageElement;
                      target.src = defaultPlaceholder;
                    }}
                  />
                </div>
                <div className="buy-home-faq-contact-info">
                  <h3 className="buy-home-faq-contact-title">Still have a questions?</h3>
                  <div className="buy-home-faq-contact-details">
                    <div className="buy-home-faq-contact-item">
                      <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                        <path d="M10.0003 4.99996V9.99996L13.3337 11.6666M18.3337 9.99996C18.3337 14.6023 14.6027 18.3333 10.0003 18.3333C5.39795 18.3333 1.66699 14.6023 1.66699 9.99996C1.66699 5.39759 5.39795 1.66663 10.0003 1.66663C14.6027 1.66663 18.3337 5.39759 18.3337 9.99996Z" stroke="#0578EC" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                      <span>Mon - Fri, 9am to 7pm</span>
                    </div>
                    <div className="buy-home-faq-contact-item">
                      <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                        <path d="M16.5625 3.75H3.4375C2.57417 3.75 1.875 4.45 1.875 5.3125V14.6875C1.875 15.55 2.575 16.25 3.4375 16.25H16.5625C17.425 16.25 18.125 15.55 18.125 14.6875V5.3125C18.125 4.44917 17.425 3.75 16.5625 3.75Z" stroke="#0578EC" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                        <path d="M4.375 6.25L10 10.625L15.625 6.25" stroke="#0578EC" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                      <span>Valentinoloanofficer@gmail.com</span>
                    </div>
                    <div className="buy-home-faq-contact-item">
                      <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                        <path d="M17.4999 14.5376C16.8894 13.9222 15.4108 13.0246 14.6928 12.6627C13.7582 12.1917 13.6811 12.154 12.9467 12.6996C12.4569 13.064 12.1311 13.3889 11.5576 13.2666C10.9832 13.1444 9.73762 12.4543 8.64631 11.3671C7.555 10.2791 6.82555 8.99658 6.70329 8.42549C6.58021 7.85358 6.91089 7.53111 7.27192 7.04043C7.78065 6.34872 7.74209 6.23303 7.3072 5.29844C6.96832 4.57145 6.04358 3.10598 5.42654 2.49796C4.76601 1.84564 4.76601 1.96133 4.34016 2.13775C3.99345 2.28373 3.66084 2.46116 3.34649 2.66781C2.73109 3.07644 2.38975 3.41614 2.15098 3.92651C1.91138 4.43688 1.80471 5.63322 3.03797 7.87409C4.27123 10.115 5.13689 11.2604 6.9273 13.0467C8.71934 14.833 10.0962 15.7939 12.1098 16.9229C14.6009 18.3178 15.556 18.0462 16.068 17.8075C16.58 17.5695 16.9214 17.2306 17.3316 16.6152C17.5386 16.3013 17.716 15.969 17.8617 15.6224C18.0389 15.199 18.1546 15.199 17.5007 14.5385L17.4999 14.5376Z" stroke="#0578EC" strokeWidth="1.4"/>
                      </svg>
                      <span>+1 (714) 655-1681</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Other Mortgage Programs Section */}
        <section className="buy-home-programs">
          <div className="buy-home-container">
            <div className="buy-home-programs-content">
              <h2 className="buy-home-programs-title">Other mortgage programs</h2>
              <p className="buy-home-programs-description">
                Lorem ipsum dolor sit amet consectetur. Massa cursus suspendisse nam malesuada habitasse enim aliquet cras. Purus gravida enim vitae tristique a porttitor sed. Pellentesque semper tincidunt viverra ac turpis justo. Ac integer ultrices euismod magnis euismod.
              </p>
              <div className="buy-home-programs-grid">
                <div className="buy-home-program-card">
                  <div className="buy-home-program-icon">
                    <svg width="48" height="48" viewBox="0 0 48 48" fill="none">
                      <path d="M24 4L6 14V24C6 32.84 11.16 41.16 24 44C36.84 41.16 42 32.84 42 24V14L24 4Z" fill="#0578EC"/>
                    </svg>
                  </div>
                  <h3 className="buy-home-program-title">Purchase New Home</h3>
                  <p className="buy-home-program-description">
                    Get expert assistance to secure the best mortgage rates and terms, guiding you through the entire home-buying process.
                  </p>
                  <button className="buy-home-cta-primary">Get your rate</button>
                </div>
                <div className="buy-home-program-card buy-home-program-card-featured">
                  <div className="buy-home-program-icon">
                    <svg width="48" height="48" viewBox="0 0 48 48" fill="none">
                      <path d="M24 4L6 14V24C6 32.84 11.16 41.16 24 44C36.84 41.16 42 32.84 42 24V14L24 4Z" fill="white"/>
                    </svg>
                  </div>
                  <h3 className="buy-home-program-title">Refinance Home</h3>
                  <p className="buy-home-program-description">
                    Refinance your existing mortgage to lower interest rates, adjust loan terms, or access your home's equity quickly.
                  </p>
                  <button className="buy-home-cta-secondary buy-home-cta-white">Get your rate</button>
                </div>
                <div className="buy-home-program-card">
                  <div className="buy-home-program-icon">
                    <svg width="48" height="48" viewBox="0 0 48 48" fill="none">
                      <path d="M24 4L6 14V24C6 32.84 11.16 41.16 24 44C36.84 41.16 42 32.84 42 24V14L24 4Z" fill="#0578EC"/>
                    </svg>
                  </div>
                  <h3 className="buy-home-program-title">Reverse Mortgage</h3>
                  <p className="buy-home-program-description">
                    A reverse mortgage allows homeowners 62+ to convert home equity into cash while staying in their property.
                  </p>
                  <button className="buy-home-cta-primary">Get your rate</button>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
      <EligibilityForm
        isOpen={isEligibilityFormOpen}
        onClose={() => setIsEligibilityFormOpen(false)}
        onSubmit={handleEligibilityFormSubmit}
      />
    </div>
  );
};

export default BuyHome;
